import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { APP_NAMESPACE } from '@/meta'

/**
 * Thème clair / sombre / automatique.
 *
 * TROIS états et non deux : « automatique » est un état à part entière, qui suit
 * le réglage du poste et change avec lui. Le confondre avec « clair » est le
 * défaut classique — l'utilisateur passe son système en sombre le soir et
 * l'application reste blanche.
 *
 * ─── Ce que ce store fait, et qu'une bascule à deux lignes ne fait pas ───
 *
 * **1. Il expose le thème RÉSOLU.** « automatique » ne suffit pas à celui qui
 * doit choisir une couleur en JavaScript : un graphique, un canevas, une carte,
 * un éditeur de code ont besoin de savoir si l'on est *effectivement* en sombre.
 * Sans `resolu`, chacun réinterroge `matchMedia` à sa façon et se désynchronise.
 *
 * **2. Il suit le système EN DIRECT.** Sans écouteur, la bascule automatique
 * jour/nuit du poste ne prend effet qu'au prochain rechargement de page.
 *
 * **3. Il se synchronise entre ONGLETS.** Changer de thème dans un onglet doit
 * le changer dans les autres : deux onglets de la même application dans deux
 * apparences différentes, c'est un défaut visible immédiatement.
 *
 * **4. Il met à jour `<meta name="theme-color">`.** Sur mobile, c'est ce qui
 * colore la barre d'adresse et la barre de statut. Sans cela, une application
 * sombre garde un bandeau blanc en haut de l'écran.
 */

export type ThemeChoice = 'light' | 'dark' | 'system'
export type ThemeResolu = 'light' | 'dark'

const CLE_STOCKAGE = `${APP_NAMESPACE}.theme`

/** Couleur de la barre d'adresse mobile, par thème résolu. */
const COULEUR_CHROME: Record<ThemeResolu, string> = {
  // Alignées sur `--color-bg` des deux thèmes de `assets/main.css`. Les répéter
  // ici est un compromis assumé : lire une variable CSS depuis JavaScript exige
  // `getComputedStyle` sur un élément déjà peint, ce qui arrive trop tard pour
  // la première peinture.
  light: '#f8fafc',
  dark: '#0b1020',
}

function lireStockage(): ThemeChoice {
  try {
    const brut = localStorage.getItem(CLE_STOCKAGE)
    if (brut === 'light' || brut === 'dark' || brut === 'system') return brut
  } catch {
    // Stockage indisponible (navigation privée, prérendu) : on suit le système.
  }
  return 'system'
}

const REQUETE_SOMBRE = '(prefers-color-scheme: dark)'

/**
 * Vrai si le poste demande un thème sombre.
 *
 * `matchMedia` peut manquer — jsdom ne l'implémente pas. Sans cette précaution,
 * la création du store lève, et tout test de composant qui lit le thème échoue
 * avec une erreur qui ne parle pas du composant testé. Le repli est « clair »,
 * qui est le défaut historique du web.
 */
function systemePrefereSombre(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false
  }
  return window.matchMedia(REQUETE_SOMBRE).matches
}

export function resoudre(choix: ThemeChoice): ThemeResolu {
  if (choix === 'system') return systemePrefereSombre() ? 'dark' : 'light'
  return choix
}

/** Pose l'attribut lu par le CSS, et la couleur de la chrome mobile. */
function appliquer(resolu: ThemeResolu): void {
  document.documentElement.dataset.theme = resolu

  let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')
  if (meta === null) {
    meta = document.createElement('meta')
    meta.name = 'theme-color'
    document.head.appendChild(meta)
  }
  meta.content = COULEUR_CHROME[resolu]
}

/**
 * Applique le thème AVANT le montage de Vue. Appelé depuis `main.ts`.
 *
 * Le script en ligne de `index.html` couvre la toute première peinture ; cette
 * fonction couvre la suite, et le cas où ce script n'a pas pu s'exécuter.
 */
export function initThemeEager(): void {
  appliquer(resoudre(lireStockage()))
}

export const useThemeStore = defineStore('theme', () => {
  const choix = ref<ThemeChoice>(lireStockage())
  /** Thème effectivement appliqué. À utiliser pour toute couleur calculée en JS. */
  const resolu = ref<ThemeResolu>(resoudre(choix.value))

  const estSombre = computed(() => resolu.value === 'dark')

  function definir(nouveau: ThemeChoice, persister = true): void {
    choix.value = nouveau
    resolu.value = resoudre(nouveau)
    appliquer(resolu.value)

    if (!persister) return
    try {
      localStorage.setItem(CLE_STOCKAGE, nouveau)
    } catch {
      // Appliqué pour cette session, simplement non mémorisé.
    }
  }

  /** Bascule clair ↔ sombre. Depuis « automatique », part de l'état effectif. */
  function toggle(): void {
    definir(estSombre.value ? 'light' : 'dark')
  }

  if (typeof window !== 'undefined') {
    // Suivi du système, en direct — quand le navigateur le permet.
    if (typeof window.matchMedia === 'function') {
      const liste = window.matchMedia(REQUETE_SOMBRE)
      const surChangement = (): void => {
        if (choix.value === 'system') definir('system')
      }
      // `addEventListener` n'existe pas sur les MediaQueryList de Safari < 14 :
      // `addListener`, déprécié, y reste le seul moyen.
      if (typeof liste.addEventListener === 'function') {
        liste.addEventListener('change', surChangement)
      } else if (typeof liste.addListener === 'function') {
        liste.addListener(surChangement)
      }
    }

    // Synchronisation entre onglets. L'événement `storage` ne se déclenche que
    // dans les AUTRES onglets — jamais dans celui qui a écrit — ce qui évite
    // toute boucle. On ne repersiste donc pas ici.
    window.addEventListener('storage', (evenement) => {
      if (evenement.key !== CLE_STOCKAGE) return
      const recu = evenement.newValue
      if (recu === 'light' || recu === 'dark' || recu === 'system') {
        definir(recu, false)
      }
    })
  }

  return { choix, resolu, estSombre, definir, toggle }
})
